<template>
    <form @submit.prevent="submitHandler" class="lg:flex">
        <div class="tra__message-form relative lg:w-2/3 lg:pr-3">
            <div>
                <button-select
                    v-model="messageable_type"
                    :options="{ contacts: 'To Contact(s)', group: 'To List' }"
                    :default="selectedOption"
                />
            </div>
            <div class="lg:flex justify-stretch">
                <div class="mt-3 lg:pr-2 md:w-full md:flex-shrink" v-if="messageable_type === 'group'">
                    <label class="w-full block text-gray-700" for="group">
                        List Recipient
                    </label>
                    <group-select v-model="group"></group-select>
                </div>
                <div class="mt-3 lg:pr-2 md:w-full md:flex-shrink" v-if="messageable_type === 'contacts'">
                    <label class="w-full block text-gray-700" for="contacts">
                        Recipient
                    </label>
                    <contact-select v-model="contacts"></contact-select>
                </div>
                <div class="mt-3 md:w-64 md:flex-grow lg:pl-2">
                    <button-select
                        v-model="send_type"
                        label="Send This Message"
                        :options="{ now: 'Now', later: 'Later', recurring: 'Recurring' }"
                        default="now"
                        button-style="buttonGroup"
                    />
                </div>
                <ul class="w-full recipient-errors" v-show="errors.contacts">
                    <li class="text-sm" style="color: rgb(245, 101, 101);">{{ errors.contacts }}</li>
                </ul>
            </div>
            <div class="-mx-4 sm:mx-0 mt-4">
                <message-form-later
                    v-if="send_type === 'later'"
                    v-model="form.send_time"
                    class="p-4 md:px-8 md:pt-6 md:pb-8 bg-blue-100 sm:rounded"
                />
                <message-form-recurring
                    v-if="send_type === 'recurring'"
                    v-model="recurringFormData"
                    :send_type="send_type"
                    class="p-4 md:px-8 md:pt-6 md:pb-8 bg-blue-100 sm:rounded"
                />
            </div>
            <div class="w-full mt-4 mb-4">
                <MessageFormEditor
                    v-model="editorFields"
                    :error="errors.unparsed_message ? errors.unparsed_message : null"
                    @image-preview="data => imagePreview = data"
                />
            </div>
            <div class="flex justify-between p-2 md:p-8 items-center bg-gray-100 rounded-lg">
                <div>
                    <template v-if="contactCount > 0 || creditCount > 0" >
                        Your message will be sent to <span class='font-bold text-gray-700'>{{ contactCount }} {{ contactCount == 1 ? "contact" : "contacts" }}</span> using <span class='font-bold text-gray-700'>{{ creditCount }} {{ creditCount == 1 ? 'credit' : 'credits' }}</span>.
                    </template>
                    <template v-else>
                        Select a recipient and type a message to get started!
                    </template>
                </div>

                <div class="">
                    <jet-button
                        :disabled="contactCount==0"
                        :loading="showLoading"
                    >
                        {{ sendButtonText }}
                    </jet-button>
                </div>
            </div>
        </div>
        <div class="lg:border-t-0 border-t border-t-1 border-t-gray-200 lg:w-1/3 lg:pl-3 pt-6 lg:pt-0 mt-6 lg:mt-0 flex">
            <MessageFormPreview
                :message="editorFields.message"
                :contact="previewContact"
                :media="imagePreview"
                :group="group"
            />
        </div>

        <message-held-for-review-modal
            v-if="messageHeldForReviewIsOpen"
            @close="closeMessageHeldForReviewModal()"
        />

        <jet-dialog-modal :show="creditOverageModal" @close="closeOverageModal()">
            <template #title>
                Credit Limit Exceeded
            </template>

            <template #content>
                <p>
                    <template v-if="$page.props.grace_period">
                        You can not exceed your credit limit with renewal turned off.
                    </template>
                    <template v-else>
                        You are only allowed to exceed your monthly/annual credit limit by 2x and sending this message would exceed that limit. Please upgrade your account to a plan with a higher credit limit, or adjust your message to use fewer credits to continue.
                    </template>
                    <br>
                </p>

                <ul class="list-disc mt-2 pl-4 mb-2">
                    <li class="font-bold" v-if="creditLimitData">
                        Remaining Credits: {{ creditLimitData.available_segments }}
                    </li>
                    <li class="font-bold">
                        Credits required to send this message: {{ creditCount }}
                    </li>
                </ul>

                <p>If you have any questions about how message credits are calculated, please review our article: <a class ='text-blue-400 underline' target="_blank" href="https://help.textspot.io/en/articles/9117502-what-are-credits-and-how-do-they-work">How do credits work?</a></p>

            </template>

            <template #footer>
                <a href="#" class="mr-2 px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900" v-on:click="closeOverageModal()" >Cancel</a>

                <Link :href="route('subscriptions.index')" class="w-full text-center">
                    <jet-secondary-button @click="closeOverageModal()">
                        View Plans
                    </jet-secondary-button>
                </Link>
            </template>
        </jet-dialog-modal>

       <your-account-is-paused-modal v-if="showYourAccountIsPausedModal" @close="showYourAccountIsPausedModal = false" />
       <trial-only-message-yourself-modal v-if="showTrialOnlyMessageYourselfModal" @close="closeTrialOnlyMessageYourselfModal" />
       <your-org-needs-to-get-verified-modal v-if="showYourOrgNeedsToGetVerifiedModal" @close="closeYourOrgNeedsToGetVerifiedModal" />
       <phone-number-pending-tcr-update v-if="showPhoneNumberPendingTcrUpdateModal" @close="showPhoneNumberPendingTcrUpdateModal = false" />
       <phone-number-not-active-modal v-if="showPhoneNumberNotActiveModal" @close="showPhoneNumberNotActiveModal = false" />
       <credit-overage-warning-modal :creditLimitAvailabilityResponse="creditLimitData" />
       <daily-limit-warning-modal v-if="showDailyLimitWarningModal" :dailyLimitAvailabilityResponse="dailyLimitData" @close="showDailyLimitWarningModal = false" />
       <message-held-on-trial-modal v-if="showMessageHeldOnTrialModal" @close="showMessageHeldOnTrialModal = false" />
        <contact-in-use-on-trial-modal v-if="showContactInUseOnTrialModal" @close="showContactInUseOnTrialModal = false" />
        <limited-contacts-on-trial-modal v-if="showLimitedContactsOnTrialModal" @close="showLimitedContactsOnTrialModal = false" />

    </form>
</template>

<script>
import MessageHeldForReviewModal from './Modals/MessageHeldForReview';
import JetDialogModal from '../Jetstream/DialogModal';
//import DatePicker from "vue2-datepicker";
//import "vue2-datepicker/index.css";
import TrialOnlyMessageYourselfModal from './Modals/TrialOnlyMessageYourselfModal.vue';
import YourOrgNeedsToGetVerifiedModal from './Modals/YourOrgNeedsToGetVerifiedModal.vue';
import YourAccountIsPausedModal from './Modals/YourAccountIsPausedModal.vue';
import PhoneNumberNotActiveModal from './Modals/PhoneNumberNotActiveModal.vue';
import PhoneNumberPendingTcrUpdate from './Modals/PhoneNumberPendingTcrUpdate.vue';
import CreditOverageWarningModal from './Modals/CreditOverageWarningModal.vue';
import DailyLimitWarningModal from './Modals/DailyLimitWarningModal.vue';
import MessageHeldOnTrialModal from './Modals/MessageHeldOnTrial.vue';
import ContactInUseOnTrialModal from './Modals/ContactInUseOnTrialModal.vue';
import LimitedContactsOnTrialModal from './Modals/LimitedContactsOnTrialModal.vue';

import JetButton from "../Jetstream/Button";
import axios from "axios";

import JetSecondaryButton from '../Jetstream/SecondaryButton';
import ContactSelect from './ContactSelect';
import MessageFormRecurring from './MessageForm/MessageFormRecurring';
import MessageFormLater from './MessageForm/MessageFormLater';
import MessageFormEditor from './MessageForm/MessageFormEditor.vue';
import MessageFormPreview from './MessageForm/MessageFormPreview.vue';
import ButtonSelect from './ButtonSelect';
import { useGeneralTableStore } from '@/Stores/general_table';
import { useGlobalStore } from '@/Stores/global';
import { useModalStore } from '@/Stores/modal';
import {mapWritableState, mapActions, mapState} from 'pinia';

import { SegmentedMessage } from 'sms-segments-calculator';


export default {
    props: ['md'],
    emits: ['success'],
    components: {
        //DatePicker,
        JetButton,
        JetDialogModal,
        JetSecondaryButton,
        ContactSelect,
        MessageFormEditor,
        MessageFormRecurring,
        MessageFormLater,
        MessageFormPreview,
        ButtonSelect,
        MessageHeldForReviewModal,
        TrialOnlyMessageYourselfModal,
        YourOrgNeedsToGetVerifiedModal,
        YourAccountIsPausedModal,
        PhoneNumberNotActiveModal,
        PhoneNumberPendingTcrUpdate,
        CreditOverageWarningModal,
        DailyLimitWarningModal,
        MessageHeldOnTrialModal,
        ContactInUseOnTrialModal,
        LimitedContactsOnTrialModal
    },
    data() {
        return {
            form: this.$inertia.form({
                contacts: [],
                group_id: undefined,
                unparsed_message: null,
                send_time: null,
                is_recurring: null,
                md_id: null, //used for editing existing messages
                recurring_params: {
                    interval: 1,
                    repeats_type: "DAILY",
                    repeats_on: [],
                    recurring_ends: "never",
                    count: 1,
                    end_date: null,
                    start_date: null
                },
                media: null,
                confirm_send: ''
            }, {
                resetOnSuccess: true,
            }),
            uploadImageFormVisible: false,
            editMode: false,
            loading: false,
            contacts: [],
            group: null,
            send_date: null,
            send_hour: "12",
            send_min: "00",
            send_period: "AM",
            send_type: "now",
            recurringFormData: null,
            phone_number: null,
            current_date: "",
            messageable_type: "contacts",
            errors: {
                contacts: null,
                unparsed_message: null,
            },
            segment_lengths: {
                sms_ucs: 70,
                sms_gsm: 160,
                mms_ucs: 1000,
                mms_gsm: 1600
            },
            sms_segment_credit: 1,
            mms_segment_credit: 3,
            messageHeldForReviewIsOpen: false,
            creditOverageModal: false,
            imagePreview: null,
            creditLimitData: null,
            dailyLimitData: null,
            editorFields: {
                message: "",
                media: null
            },
            selectedOption: "contacts",
            showTrialOnlyMessageYourselfModal: false,
            showYourOrgNeedsToGetVerifiedModal: false,
            showYourAccountIsPausedModal: false,
            showPhoneNumberNotActiveModal: false,
            showPhoneNumberPendingTcrUpdateModal: false,
            showMessageHeldOnTrialModal: false,
            showDailyLimitWarningModal: false,
            showContactInUseOnTrialModal: false,
            showLimitedContactsOnTrialModal: false,
        };
    },
    created() {
        let params = new URLSearchParams(document.location.search);
        if (params.has("list_id")) {
            this.selectedOption = "group";
            this.messageable_type = "group";
        }
    },
    mounted() {
        if (this.md) {
            this.editMode = true;
            this.form.md_id = this.md.id;
            if (this.md.recurring) {
                this.send_type = "recurring";
            } else {
                this.send_type = "later";
            }

            let [datePart, time, period] = this.md.scheduled_send_time.split(' ');

            // Parsing the date
            let [month, day, year] = datePart.split('-');
            this.send_date = `${year}-${month}-${day}`;

            // Parsing the time
            let [hour, min] = time.split(':');

            this.send_hour = hour;
            this.send_min = min;
            this.send_period = period.toUpperCase();  // Making sure the period is in uppercase
            this.editorFields.message = this.md.unparsed_message;
            this.editorFields.media = this.md.media;
            if (this.md.group) {
                this.form.group_id = this.md.group.id;
                this.group = this.md.group;
                this.messageable_type="group";
            } else {
                this.contacts = this.md.contacts;
                this.messageable_type="contacts";
            }
            if (this.md.recurring) {
                const recurring_rule = this.md.recurring_rule;

                // Split the string at newline to separate DTSTART from RRULE
                const parts = recurring_rule.split('\n');

                // Extract DTSTART value
                const dtStartMatch = parts[0].match(/DTSTART:(\d+T\d+Z)/);
                const dtStart = dtStartMatch ? dtStartMatch[1] : null;

                // Extract RRULE components
                const rruleString = parts[1].replace('RRULE:', '');
                const rruleComponents = rruleString.split(';').reduce((acc, component) => {
                    const [key, value] = component.split('=');
                    acc[key] = value;
                    return acc;
                }, {});

                this.recurringFormData = {
                    recurring_ends: this.md.recurring_end_date ? 'on_date' : 'never',
                    interval: rruleComponents.INTERVAL || 1,
                    start_date: this.md.scheduled_send_time,
                    repeats_type: rruleComponents.FREQ,
                    count: rruleComponents.COUNT,
                    end_date: this.md.recurring_end_date,
                    repeats_on: rruleComponents.BYDAY ? rruleComponents.BYDAY.split(",") : [],
                }
            }

        } else {
            this.setDefaultDateTime();
        }

        document.querySelector('.editor-textarea').focus();
    },
  computed: {
        ...mapState(useGlobalStore, [
            'phoneNumberPendingTcrUpdate',
            'isPhoneNumberActive',
        ]),
        ...mapState(useModalStore,{
            creditOverageWarningModalResolve: 'resolve',
        }),
        showLoading () {
            return this.loading || this.form.processing;
        },
        sendButtonText() {
            if (this.showLoading) {
                return "Processing...";
            }

            if (this.md) {
                return "Update Message";
            }
            return this.send_type === "now" ? "Send" : "Schedule";
        },
        sendTime() {
            if (this.send_type === "now") {
                return null; // sending null will create a current timestamp once submitted to the backend
            }

            // @todo - add null handling here if the date hasn't been set yet
            if (this.send_type === "later") {
                let hour = this.send_hour === "12" ? "00" : this.send_hour;

                if (this.send_period === "PM") {
                    hour = parseInt(hour, 10) + 12;
                }

                return `${this.send_date} ${hour}:${this.send_min}`;
            }
        },
        messageSegments() {
            return new SegmentedMessage(this.editorFields.message);
        },
        characterCount() {
            return this.editorFields.message.length;
        },
        contactCount() {
            if ( this.messageable_type === 'contacts' ) {
                return this.contacts.length;
            }
            if (this.group) {
               return this.group.contactsCount;
            }
            return 0;
        },
        /*
        Estimated number of credits to be used by the message
         */
        creditCount() {
            if (this.editorFields.media && !_.isEmpty(this.editorFields.media)) {
                // divide character count by segment length and round up
                // to nearest integer to get number of segments, and then
                // we multiply that by the number of credits per segment to
                // get total estimated credit cost
                if ( this.messageSegments.encodingName === 'GSM-7' && this.characterCount ) {
                    return Math.ceil(this.characterCount / this.segment_lengths.mms_gsm) * this.mms_segment_credit * this.contactCount;
                }

                if ( this.messageSegments.encodingName === 'UCS-2' && this.characterCount ) {
                    return Math.ceil(this.characterCount / this.segment_lengths.mms_ucs) * this.mms_segment_credit * this.contactCount;
                }

                if ( !this.characterCount) {
                  return this.mms_segment_credit * this.contactCount;
                }
            } else {
                return this.messageSegments.segmentsCount * this.contactCount;
            }
        },
        creditTooltip () {
            if (this.messageable_type === 'contacts') {
                return `Calculated from ${this.contacts.length} ${this.contacts.length == 1 ? 'contact' : 'contacts'} at ${this.messageSegments.segmentsCount} ${this.messageSegments.segmentsCount == 1 ? 'segment' : 'segments'} each.`
            }

            if (this.messageable_type === 'group' && this.group) {
                if (this.group.contactsCount != this.group.goodContactsCount) {
                    return `Your list contains ${this.group.contactsCount} ${this.group.contactsCount == 1 ? 'contact' : 'contacts'}, but ${this.group.contactsCount - this.group.goodContactsCount} can not be messaged and are excluded from the calculation. So ${this.group.goodContactsCount} ${this.group.goodContactsCount == 1 ? 'contact' : 'contacts'} at ${this.messageSegments.segmentsCount} ${this.messageSegments.segmentsCount == 1 ? 'segment' : 'segments'} each.`
                } else {
                    // our list contains all good contacts
                    return `Your list contains ${this.group.contactsCount} ${this.group.contactsCount == 1 ? 'contact' : 'contacts'} at ${this.messageSegments.segmentsCount} ${this.messageSegments.segmentsCount == 1 ? 'segment' : 'segments'} each.`
                }
            }
        },
        previewContact() {
            if ( this.contactCount > 0 && this.messageable_type === 'contacts' ) {
                return this.contacts[0];
            } else if (this.contactCount > 0 && this.messageable_type === 'groups' ) {
                // @todo return first group contact
                return null;
            }

            return null;
        },
    },
    methods: {
        ...mapActions(useGeneralTableStore, [
            'clearData', 'fetchData',
        ]),
        ...mapActions(useModalStore, {
            showCreditOverageWarningModal: 'showModal',
        }),
        setDefaultDateTime() {
            this.send_date = new Date().toISOString().split('T')[0];
            this.current_date = new Date();
            this.current_date.setHours(0, 0, 0, 0);
            // Set the default end date to six months in the future

            let now = new Date();
            let nextHour = now.getHours() + 1; // Increment by 1 hour

            // Adjust to 12-hour format
            if (nextHour > 23) nextHour = 0; // If it's 24 (midnight), reset to 0
            let period = nextHour >= 12 ? "PM" : "AM";

            if (nextHour > 12) nextHour -= 12;
            if (nextHour === 0) nextHour = 12; // For 12AM

            this.send_hour = String(nextHour);
            this.send_min = "00";
            this.send_period = period;
            this.end_date = new Date().setMonth(this.current_date.getMonth() + 6);
        },
        messageableClass(messageable_type) {
            return this.messageable_type === messageable_type
                ? "bg-blue-400 text-blue-100 border-blue-400 border"
                : "bg-blue-100 text-blue-400 border-blue-400 border";
        },
        scheduledSendTimeIsInThePast() {
            if (this.send_type === "later") {
                const now = new Date();
                const sendTime = new Date(this.send_time);
                return sendTime < now;
            }

            return false;
        },
        successMessage() {
            const type = this.editMode ? "updated" : (this.send_type === "now" ? "queued for sending" : "scheduled");

            this.$toasted.show(`Your message has been successfully ${type}!`, {
                position: "bottom-right",
                type: "success",
                duration: 3000
            });
        },
        errorMessage(error) {
            this.errors = error;
            let errorMessage = "Uh oh! Something went wrong.";

            if (error.message == 'held_for_review' ) {
                this.messageHeldForReviewIsOpen = true;
                return;
            }

            if ( error.message ) {
              errorMessage = error.display_message;
            }

            this.$toasted.show(errorMessage, {
                position: "bottom-right",
                type: "error",
                duration: 3000
            });
        },
        resetValues() {
            this.editorFields.message = "";
            this.form.unparsed_message = null;
            this.errors = {};
        },
        closeMessageHeldForReviewModal() {
            this.messageHeldForReviewIsOpen = false;
            this.resetValues();
        },
        closeOverageModal() {
            this.creditOverageModal = false;
        },
        closeTrialOnlyMessageYourselfModal() {
            this.showTrialOnlyMessageYourselfModal = false;
        },
        closeYourOrgNeedsToGetVerifiedModal() {
            this.showYourOrgNeedsToGetVerifiedModal = false;
        },
        async submitHandler() {
            this.loading = true;

            if (this.scheduledSendTimeIsInThePast()) {
                if (
                    !window.confirm(
                        "Your scheduled send time is in the past. This will result in your message being sent instantly. Would you like to send this message immediately?"
                    )
                ) {
                    this.loading = false;
                    return;
                }
            }

            if (this.$page.props.paused) {
                this.showYourAccountIsPausedModal = true;
                this.loading = false;
                return;
            }

            if (this.$page.props.auth.user.organization.suspended) {
                this.$toasted.show("Your organization is currently suspended and cannot send messages.", {
                    position: "bottom-right",
                    type: "error",
                    duration: 3000
                });
                this.loading = false;
                return;
            }

            if (this.$page.props.active_trial) {
                //this is a trial account, let's make sure they don't have any pending messages under review
                let pendingStatus = await axios.get(route('organizations.activeTrialPendingReview'));
                if (pendingStatus.data.status) {
                    this.showMessageHeldOnTrialModal = true;
                    this.loading = false;
                    return;
                }

                // Check trial contact limits
                let trialContactCheck;
                if (this.messageable_type == 'group') {
                    trialContactCheck = await axios.get(route('messages.checkTrialContactLimit'), {
                        params: { group_tra_id: this.group.tra_id }
                    });
                } else if (this.messageable_type == 'contacts') {
                    let contactTraIds = this.contacts.map(contact => contact.tra_id);
                    trialContactCheck = await axios.get(route('messages.checkTrialContactLimit'), {
                        params: { contact_tra_ids: contactTraIds }
                    });
                }

                if (trialContactCheck && !trialContactCheck.data.approved) {
                    if (trialContactCheck.data.reason_code === 'CONTACT_IN_USE_BY_OTHER_TRIAL') {
                        this.showContactInUseOnTrialModal = true;
                    } else if (trialContactCheck.data.reason_code === 'TRIAL_CONTACT_LIMIT_EXCEEDED') {
                        this.showLimitedContactsOnTrialModal = true;
                    }
                    this.loading = false;
                    return;
                }
            }

            if (this.$page.props.subscribed && !this.$page.props.a2p_status_good_to_send) {
                this.showYourOrgNeedsToGetVerifiedModal = true;
                this.loading = false;
                return;
            }

            if (this.phoneNumberPendingTcrUpdate) {
                this.showPhoneNumberPendingTcrUpdateModal = true;
                this.loading = false;
                return;
            }

            if (!this.isPhoneNumberActive) {
                this.showPhoneNumberNotActiveModal = true;
                this.loading = false;
                return;
            }

            // Check daily credit limit for A2P send-limited organizations FIRST
            if (this.$page.props.a2p_send_limited) {
                let dailyLimitResponse = await axios.get('/api/messages/check-daily-limit', {
                    params: {
                        credits_considering: this.creditCount
                    }
                });
                this.dailyLimitData = dailyLimitResponse.data;
                
                if (!dailyLimitResponse.data.approved) {
                    this.showDailyLimitWarningModal = true;
                    this.loading = false;
                    return;
                }
            }

            let creditResponse = await axios.get('/api/messages/check-credits', {
              params: {
                estimated_credits: this.creditCount
              }
            });
            this.creditLimitData = creditResponse.data;
            let approved = creditResponse.data.approved;

            //we need to check credit limit availability
            if (!approved) {
              this.creditOverageModal = true;
              this.loading = false;
              return;
            }

            if (creditResponse.data.overage_warning) {
                let response = await this.showCreditOverageWarningModal();
                if (!response) {
                    //they closed the modal
                    this.loading = false;
                    return;
                }
            }

            //reinitialize form variables
            this.form.contacts = null;
            this.form.group_id = null;

            if (this.messageable_type == 'contacts') {
                this.form.contacts = this.contacts.map(a => a['id']);
            } else if (this.messageable_type == 'group') {
                this.form.group_id = this.group.id; //map(value => value.id);
            }


            this.form.post(route("md.create.submit"), {
                onSuccess: () => {
                    this.successMessage();
                    
                    if (this.editMode) {
                        this.$inertia.visit(route('md.scheduled'))
                        return;
                    }

                    this.resetValues();
                    this.loading = false;
                    this.clearData({resetPage: false});
                    this.$emit('success');
                },
                onError: error => {
                    this.loading = false;
                    this.errorMessage(error);
                }
            });

        }
    },
    watch: {
        editorFields: {
            handler(value) {
                this.form.unparsed_message = value.message;
                this.form.media = value.media;
            },
            deep: true
        },
        messageable_type(value) {
          if (value == 'group')   {
            //clear out contacts
            this.contacts = [];
          } else if (value == 'contacts') {
            //clear out groups
            this.group = null;
          }
        },
        sendTime(value) {
            this.form.send_time = value;
        },
        send_type(value) {
            this.form.is_recurring = value === "recurring";
        },
        recurringFormData: {
            handler(value) {
                this.form.recurring_params.interval = value.interval;
                this.form.recurring_params.repeats_type = value.repeats_type;
                if (value.repeats_on) {
                    this.form.recurring_params.repeats_on = value.repeats_on;
                }
                this.form.recurring_params.recurring_ends = value.recurring_ends;
                this.form.recurring_params.count = value.count;
                this.form.recurring_params.end_date = value.end_date;
                this.form.recurring_params.start_date = value.start_date;
            },
            deep: true
        },
    }
};
</script>

<style>
.toasted.success {
    @apply bg-green-500 text-white font-bold shadow rounded !important;
}

.toasted.error {
    @apply bg-red-500 text-white font-bold shadow rounded !important;
}

.mx-datepicker {
    width: 100% !important;
}

.mx-input {
    @apply border-2 border-gray-300;
    height: 40px;
}

.input-tel__input,
.country-selector__input {
    @apply border-2 border-gray-300 !important;
}

.input-tel__input {
    @apply border-l-0 !important;
}

select {
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%2371717a' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
    background-position: right 0.5rem center;
    background-repeat: no-repeat;
    background-size: 1.5em 1.5em;
    padding-right: 2.5rem;
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
}

.occurrences input {
    @apply w-1/3;

    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

.vs__dropdown-toggle {
    height: 42px;
}

.triangle {
    position: absolute;
    bottom: -33px;
    height: 25px;
    width: 40px;
    background: #ebf8ff;
    z-index: 1;
    display: block;
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
    pointer-events: none;
}

</style>
